import type { Cell, ID, Template } from './types';
import { uid } from './utils';

export function createGrid(cols: number, rows: number): Template {
  const cells: Cell[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push({ id: uid('cell'), c, r, cw: 1, rh: 1 });
    }
  }
  return { cols, rows, cells };
}

/** Copy a saved layout as a new instance so it never reuses live cell ids. */
export function instantiateTemplate(template: Template): Template {
  return {
    cols: template.cols,
    rows: template.rows,
    cells: template.cells.map((cell) => ({ ...cell, id: uid('cell') })),
  };
}

/** Cell id occupying each grid unit, or null for a hole. */
export function occupancy(t: Template): (ID | null)[][] {
  const grid: (ID | null)[][] = Array.from({ length: t.rows }, () =>
    Array<ID | null>(t.cols).fill(null),
  );
  for (const cell of t.cells) {
    for (let r = cell.r; r < cell.r + cell.rh; r++) {
      for (let c = cell.c; c < cell.c + cell.cw; c++) {
        if (r >= 0 && r < t.rows && c >= 0 && c < t.cols) grid[r][c] = cell.id;
      }
    }
  }
  return grid;
}

/** A template is valid when every grid unit is covered exactly once. */
export function isValid(t: Template): boolean {
  const seen = Array.from({ length: t.rows }, () => Array(t.cols).fill(0));
  for (const cell of t.cells) {
    if (cell.c < 0 || cell.r < 0 || cell.c + cell.cw > t.cols || cell.r + cell.rh > t.rows) {
      return false;
    }
    for (let r = cell.r; r < cell.r + cell.rh; r++) {
      for (let c = cell.c; c < cell.c + cell.cw; c++) seen[r][c] += 1;
    }
  }
  return seen.every((row) => row.every((v) => v === 1));
}

function bounds(cells: Cell[]) {
  const c0 = Math.min(...cells.map((c) => c.c));
  const r0 = Math.min(...cells.map((c) => c.r));
  const c1 = Math.max(...cells.map((c) => c.c + c.cw));
  const r1 = Math.max(...cells.map((c) => c.r + c.rh));
  return { c: c0, r: r0, cw: c1 - c0, rh: r1 - r0 };
}

/** Merging is allowed only when the selection is a gap-free rectangle. */
export function canMerge(t: Template, ids: ID[]): boolean {
  if (ids.length < 2) return false;
  const set = new Set(ids);
  const cells = t.cells.filter((c) => set.has(c.id));
  if (cells.length !== set.size) return false;
  const b = bounds(cells);
  const area = cells.reduce((sum, c) => sum + c.cw * c.rh, 0);
  return area === b.cw * b.rh;
}

/**
 * Merge a rectangular selection into one cell. The first selected cell keeps
 * its id so its image (and adjustments) survive the merge.
 */
export function mergeCells(t: Template, ids: ID[]): Template {
  if (!canMerge(t, ids)) return t;
  const set = new Set(ids);
  const selected = t.cells.filter((c) => set.has(c.id));
  const keepId = ids[0];
  const b = bounds(selected);
  const merged: Cell = { id: keepId, ...b };
  const cells = t.cells.filter((c) => !set.has(c.id));
  cells.push(merged);
  return sortCells({ ...t, cells });
}

/** Double the resolution of one axis so a 1-unit cell can still be split. */
export function refineAxis(t: Template, axis: 'x' | 'y'): Template {
  const cells = t.cells.map((cell) =>
    axis === 'x'
      ? { ...cell, c: cell.c * 2, cw: cell.cw * 2 }
      : { ...cell, r: cell.r * 2, rh: cell.rh * 2 },
  );
  return {
    cols: axis === 'x' ? t.cols * 2 : t.cols,
    rows: axis === 'y' ? t.rows * 2 : t.rows,
    cells,
  };
}

/** Split one cell in half. Returns the new template and the new cell's id. */
export function splitCell(
  t: Template,
  id: ID,
  dir: 'vertical' | 'horizontal',
): { template: Template; newId: ID | null } {
  const axis = dir === 'vertical' ? 'x' : 'y';
  let work = t;
  let cell = work.cells.find((c) => c.id === id);
  if (!cell) return { template: t, newId: null };

  const span = axis === 'x' ? cell.cw : cell.rh;
  if (span < 2) {
    work = refineAxis(work, axis);
    cell = work.cells.find((c) => c.id === id)!;
  }

  const cw = axis === 'x' ? Math.floor(cell.cw / 2) : cell.cw;
  const rh = axis === 'y' ? Math.floor(cell.rh / 2) : cell.rh;
  const newId = uid('cell');

  const first: Cell = { ...cell, cw, rh };
  const second: Cell =
    axis === 'x'
      ? { id: newId, c: cell.c + cw, r: cell.r, cw: cell.cw - cw, rh: cell.rh }
      : { id: newId, c: cell.c, r: cell.r + rh, cw: cell.cw, rh: cell.rh - rh };

  const cells = work.cells.map((c) => (c.id === id ? first : c));
  cells.push(second);
  return { template: simplify(sortCells({ ...work, cells })), newId };
}

/** Reduce grid resolution when every cell edge lands on a coarser lattice. */
export function simplify(t: Template): Template {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const reduce = (values: number[], total: number) =>
    values.reduce((acc, v) => gcd(acc, v), total);

  const gx = reduce(t.cells.flatMap((c) => [c.c, c.cw]), t.cols);
  const gy = reduce(t.cells.flatMap((c) => [c.r, c.rh]), t.rows);
  if (gx <= 1 && gy <= 1) return t;

  return {
    cols: t.cols / (gx || 1),
    rows: t.rows / (gy || 1),
    cells: t.cells.map((c) => ({
      ...c,
      c: c.c / (gx || 1),
      cw: c.cw / (gx || 1),
      r: c.r / (gy || 1),
      rh: c.rh / (gy || 1),
    })),
  };
}

export function sortCells(t: Template): Template {
  const cells = t.cells.slice().sort((a, b) => a.r - b.r || a.c - b.c);
  return { ...t, cells };
}

/** Reading order — used by auto-fill and keyboard navigation. */
export function cellsInOrder(t: Template): Cell[] {
  return sortCells(t).cells;
}

interface Spec {
  cols: number;
  rows: number;
  areas: [number, number, number, number][];
}

function fromSpec(spec: Spec): Template {
  return {
    cols: spec.cols,
    rows: spec.rows,
    cells: spec.areas.map(([c, r, cw, rh]) => ({ id: uid('cell'), c, r, cw, rh })),
  };
}

function grid(cols: number, rows: number): Spec {
  const areas: [number, number, number, number][] = [];
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) areas.push([c, r, 1, 1]);
  return { cols, rows, areas };
}

export const TEMPLATE_PRESETS: { id: string; name: string; spec: Spec }[] = [
  { id: 'single', name: 'Single', spec: grid(1, 1) },
  { id: 'duo-v', name: 'Duo', spec: grid(2, 1) },
  { id: 'duo-h', name: 'Stack', spec: grid(1, 2) },
  { id: 'grid-2x2', name: 'Quad', spec: grid(2, 2) },
  { id: 'grid-3x3', name: 'Nine', spec: grid(3, 3) },
  { id: 'grid-4x4', name: 'Sixteen', spec: grid(4, 4) },
  {
    id: 'hero-top',
    name: 'Hero',
    spec: { cols: 3, rows: 3, areas: [[0, 0, 3, 2], [0, 2, 1, 1], [1, 2, 1, 1], [2, 2, 1, 1]] },
  },
  {
    id: 'hero-left',
    name: 'Feature',
    spec: { cols: 3, rows: 2, areas: [[0, 0, 2, 2], [2, 0, 1, 1], [2, 1, 1, 1]] },
  },
  {
    id: 'center-stage',
    name: 'Center',
    spec: {
      cols: 4,
      rows: 4,
      areas: [
        [0, 0, 1, 1], [1, 0, 2, 1], [3, 0, 1, 1],
        [0, 1, 1, 2], [1, 1, 2, 2], [3, 1, 1, 2],
        [0, 3, 1, 1], [1, 3, 2, 1], [3, 3, 1, 1],
      ],
    },
  },
  {
    id: 'mosaic',
    name: 'Mosaic',
    spec: {
      cols: 4,
      rows: 4,
      areas: [
        [0, 0, 2, 2], [2, 0, 1, 1], [3, 0, 1, 1],
        [2, 1, 2, 1],
        [0, 2, 1, 1], [1, 2, 1, 1], [2, 2, 2, 2],
        [0, 3, 2, 1],
      ],
    },
  },
  {
    id: 'filmstrip',
    name: 'Filmstrip',
    spec: { cols: 5, rows: 1, areas: [[0, 0, 1, 1], [1, 0, 1, 1], [2, 0, 1, 1], [3, 0, 1, 1], [4, 0, 1, 1]] },
  },
  {
    id: 'column-trio',
    name: 'Columns',
    spec: { cols: 3, rows: 1, areas: [[0, 0, 1, 1], [1, 0, 1, 1], [2, 0, 1, 1]] },
  },
  {
    id: 'banner',
    name: 'Banner',
    spec: { cols: 3, rows: 3, areas: [[0, 0, 1, 1], [1, 0, 1, 1], [2, 0, 1, 1], [0, 1, 3, 1], [0, 2, 1, 1], [1, 2, 1, 1], [2, 2, 1, 1]] },
  },
  {
    id: 'pinwheel',
    name: 'Pinwheel',
    spec: {
      cols: 3,
      rows: 3,
      areas: [[0, 0, 2, 1], [2, 0, 1, 2], [0, 1, 1, 2], [1, 1, 1, 1], [2, 2, 1, 1], [1, 2, 1, 1]],
    },
  },
  {
    id: 'six-mixed',
    name: 'Editorial',
    spec: {
      cols: 6,
      rows: 4,
      areas: [[0, 0, 4, 3], [4, 0, 2, 1], [4, 1, 2, 1], [4, 2, 2, 2], [0, 3, 2, 1], [2, 3, 2, 1]],
    },
  },
  {
    id: 'ladder',
    name: 'Ladder',
    spec: { cols: 2, rows: 3, areas: [[0, 0, 1, 1], [1, 0, 1, 2], [0, 1, 1, 2], [1, 2, 1, 1]] },
  },
];

export function buildPreset(id: string): Template | null {
  const preset = TEMPLATE_PRESETS.find((p) => p.id === id);
  return preset ? fromSpec(preset.spec) : null;
}

export { fromSpec as templateFromSpec };
